import Head from 'next/head';
import React from 'react';

import type { FontFaceSpec, FontLoad } from './types';

/**
 * `font-display` values from the CSS Fonts L4 spec. Controls what the
 * browser does while the font binary is still loading:
 *   - `auto`     — UA-decided.
 *   - `block`    — invisible text for ~3s, then fallback (use for icon fonts).
 *   - `swap`     — fallback immediately, swap when ready (default; best for text).
 *   - `fallback` — short block, short swap window, then locked.
 *   - `optional` — UA may skip the download on slow networks.
 */
export type FontDisplay = 'auto' | 'block' | 'swap' | 'fallback' | 'optional';

interface FontProviderProps {
  /**
   * Fonts to load. Each entry pairs a `family` name with the exact `faces`
   * the consumer needs — webpack only bundles what's referenced, so picking
   * `[ppMoriRegular, ppMoriBold]` ships just those weights.
   */
  fonts: FontLoad[];
  /**
   * Optional CDN/host prefix prepended to each face's `src.file`. Faces whose
   * `file` is already absolute (`http(s)://`, `/`, or `data:`) are emitted
   * verbatim — that's the path variants take when their files come from a
   * webpack `import` (the bundler hands us a resolved/inlined URL). Trailing
   * slash on baseUrl is fine; we trim it.
   */
  baseUrl?: string;
  /**
   * `font-display` applied to every emitted @font-face. Defaults to `'swap'`
   * (best UX for body text — invisible flash avoided).
   */
  fontDisplay?: FontDisplay;
  /**
   * When true, the wrapper sets `font-family` on itself so every descendant
   * inherits it. Defaults to false — consumers opt in per-element via
   * `style={{ fontFamily }}` or a Tailwind class.
   */
  autoApply?: boolean;
  /** Extra fallback families appended after the primary in `font-family`. */
  fallbacks?: string[];
  children: React.ReactNode;
}

const stripTrailingSlash = (url: string): string =>
  url.endsWith('/') ? url.slice(0, -1) : url;

const isAbsoluteUrl = (file: string): boolean =>
  /^(https?:)?\/\//.test(file) ||
  file.startsWith('/') ||
  file.startsWith('data:');

const resolveUrl = (baseUrl: string, file: string): string => {
  if (isAbsoluteUrl(file)) return file;
  return baseUrl === '' ? file : `${baseUrl}/${file}`;
};

const buildFaceCss = (
  family: string,
  baseUrl: string,
  face: FontFaceSpec,
  fontDisplay: FontDisplay,
): string => {
  const src = face.src
    .map(s => `url('${resolveUrl(baseUrl, s.file)}') format('${s.format}')`)
    .join(', ');
  return `@font-face{font-family:'${family}';src:${src};font-weight:${String(face.weight)};font-style:${face.style};font-display:${fontDisplay};}`;
};

const buildCss = (
  loads: FontLoad[],
  baseUrl: string,
  fontDisplay: FontDisplay,
): string => {
  const trimmed = stripTrailingSlash(baseUrl);
  return loads
    .flatMap(load =>
      load.faces.map(face =>
        buildFaceCss(load.family, trimmed, face, fontDisplay),
      ),
    )
    .join('');
};

export const FontProvider: React.FC<FontProviderProps> = ({
  fonts,
  baseUrl = '',
  fontDisplay = 'swap',
  autoApply = false,
  fallbacks = ['system-ui', 'sans-serif'],
  children,
}) => {
  const css = buildCss(fonts, baseUrl, fontDisplay);
  const familyStack = [
    ...fonts.map(load => `'${load.family}'`),
    ...fallbacks,
  ].join(', ');

  return (
    <>
      <Head>
        <style
          data-font-provider=""
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: css }}
        />
      </Head>
      {autoApply ? (
        <div style={{ fontFamily: familyStack }}>{children}</div>
      ) : (
        <>{children}</>
      )}
    </>
  );
};
